import { isPlatformBrowser } from "@angular/common";
import { AfterViewInit, Directive, Inject, NgZone, OnDestroy, PLATFORM_ID, forwardRef } from "@angular/core";
import { LIBS_PATH } from "../../common/config";
import { ScriptHelper } from "../../utility";

declare var am5: any;
declare var am5themes_Animated: any;
declare var am5themes_Micro: any;
declare var am5xy: any;
declare var am5percent: any;

@Directive()
export abstract class AmChart5Component implements AfterViewInit, OnDestroy {

    protected root!: any;
    protected am5: any;
    protected am5xy: any;
    protected am5percent: any;
    protected chartId: string;

    private readonly LINK_SOURCE = LIBS_PATH.AMCHARTS_5;
    private readonly LINK_LIBRARIES = {
        'xy': LIBS_PATH.AMCHARTS_5_XY,
        'percent': LIBS_PATH.AMCHARTS_5_PERCENT
    };
    private readonly LINK_THEMES = {
        'animated': LIBS_PATH.AMCHARTS_5_THEMES_ANIMATED,
        'micro': LIBS_PATH.AMCHARTS_5_THEMES_MICRO
    };

    private themeReference: any;

    static nextId = 0;

    constructor(
        @Inject(PLATFORM_ID) private platformId: Object,
        @Inject(forwardRef(() => NgZone)) protected zone: NgZone,
        private libraries: string[] = ['xy'],
        private theme: string = 'animated',
    ) {
        this.chartId = `${this.getChartId()}-${++AmChart5Component.nextId}`;
    }

    ngAfterViewInit(): void {
        let resources = this.initLibrariesArray();
        Promise.all(resources.map(src => ScriptHelper.append(src, document.head, false))).then(() => {
            this.browserOnly(() => {
                this.am5 = am5;
                this.am5.addLicense("AM5C429728080");
                if (this.libraries.includes('xy')) {
                    this.am5xy = am5xy;
                }
                if (this.libraries.includes('percent')) {
                    this.am5percent = am5percent;
                }
                if (this.theme == 'micro') {
                    this.themeReference = am5themes_Micro;
                } else {
                    this.themeReference = am5themes_Animated;
                }
                this.initAmCharts();
                this.initChart();
            })
        });
    }

    ngOnDestroy(): void {
        // Clean up chart when the component is removed
        this.browserOnly(() => {
            if (this.root) {
                this.root.dispose();
            }
        });
    }

    protected resizeChart(): void {
        this.root?.resize();
        this.root?.markDirtyGlobal?.();
        this.root?._renderFrame?.(performance.now());
    }

    private initLibrariesArray(): string[] {
        let resources = [this.LINK_SOURCE];
        resources.push(...this.libraries.map(l => this.LINK_LIBRARIES[l]).filter(l => l));
        resources.push(this.LINK_THEMES[this.theme] || this.LINK_THEMES['animated']);
        return resources;
    }

    protected abstract getChartId(): string;

    private initAmCharts(): void {
        this.root = this.am5.Root.new(this.chartId);
        this.root.setThemes([this.themeReference.new(this.root)]);
    }

    protected abstract initChart(): void;

    // amCharts renders on canvas and cannot resolve var(--x), so CSS variables are read from the computed style
    // of the chart container, which inherits them from the page
    protected getCssColor(variable: string): any {
        const value = this.getCssValue(variable);
        return value ? this.am5.Color.fromString(value) : undefined;
    }

    protected getCssOpacity(variable: string): number | undefined {
        const raw = this.getCssValue(variable);
        const value = parseFloat(raw);
        if (isNaN(value)) {
            return undefined;
        }
        // CSS opacity accepts both 0.6 and 60%
        return raw.endsWith('%') ? value / 100 : value;
    }

    // Drops undefined entries so that a missing variable leaves the amCharts default untouched
    protected definedSettings(settings: { [key: string]: any }): { [key: string]: any } {
        return Object.keys(settings).reduce((result, key) => {
            if (settings[key] !== undefined) {
                result[key] = settings[key];
            }
            return result;
        }, {} as { [key: string]: any });
    }

    protected applyChartBackground(chart: any): void {
        const fill = this.getCssColor('--chart-background-color');
        const fillOpacity = this.getCssOpacity('--chart-background-opacity');
        if (fill || fillOpacity !== undefined) {
            chart.set('background', this.am5.Rectangle.new(this.root, this.definedSettings({ fill, fillOpacity })));
        }
    }

    protected applyDataLabelStyle(label: any): void {
        label.setAll(this.definedSettings({
            fill: this.getCssColor('--chart-data-label-color'),
            fillOpacity: this.getCssOpacity('--chart-data-label-opacity')
        }));
    }

    private getCssValue(variable: string): string {
        const element = document.getElementById(this.chartId) || document.documentElement;
        return getComputedStyle(element).getPropertyValue(variable).trim();
    }

    // Run the function only in the browser
    protected browserOnly(f: () => void): void {
        if (isPlatformBrowser(this.platformId)) {
            this.zone.runOutsideAngular(() => {
                f();
            });
        }
    }
}
